import { UserGroupIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';

export type UserType = 'parent' | 'camp-owner';

interface UserTypeSelectorProps {
  selectedTypes: UserType[];
  onChange: (types: UserType[]) => void;
  error?: string;
}

/**
 * UserTypeSelector Component
 * 
 * Multi-select component for choosing user type during registration.
 * Users can select parent, camp-owner, or both.
 * 
 * Following CampWhiz design patterns and accessible multi-select best practices.
 */
export default function UserTypeSelector({ selectedTypes, onChange, error }: UserTypeSelectorProps) {
  const toggleType = (type: UserType) => {
    if (selectedTypes.includes(type)) {
      // Remove type if already selected
      onChange(selectedTypes.filter(t => t !== type));
    } else {
      // Add type if not selected
      onChange([...selectedTypes, type]);
    }
  };

  const isSelected = (type: UserType) => selectedTypes.includes(type);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        I am a...
      </label>
      <p className="text-xs text-gray-600 mb-3">
        Select all that apply
      </p>
      
      <div className="space-y-3" role="group" aria-label="User type selection">
        {/* Parent Option */}
        <button
          type="button"
          onClick={() => toggleType('parent')}
          data-testid="user-type-parent"
          className={`
            w-full flex items-start gap-3 p-4 rounded-xl border-2 transition-all
            ${isSelected('parent')
              ? 'border-primary bg-primary/5'
              : 'border-gray-300 bg-white hover:border-gray-400'
            }
          `}
          role="checkbox"
          aria-checked={isSelected('parent')}
          aria-label="Parent"
        >
          <div className={`
            flex-shrink-0 h-6 w-6 rounded-full border-2 flex items-center justify-center
            ${isSelected('parent')
              ? 'border-primary bg-primary'
              : 'border-gray-300'
            }
          `}>
            {isSelected('parent') && (
              <svg className="h-4 w-4 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M5 13l4 4L19 7"></path>
              </svg>
            )}
          </div>
          
          <div className="flex-1 text-left">
            <div className="flex items-center gap-2">
              <UserGroupIcon className="h-5 w-5 text-primary" aria-hidden="true" />
              <span className="font-medium text-gray-900">Parent</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Looking for camps for my children
            </p>
          </div>
        </button>

        {/* Camp Owner Option */}
        <button
          type="button"
          onClick={() => toggleType('camp-owner')}
          data-testid="user-type-camp-owner"
          className={`
            w-full flex items-start gap-3 p-4 rounded-xl border-2 transition-all
            ${isSelected('camp-owner')
              ? 'border-primary bg-primary/5'
              : 'border-gray-300 bg-white hover:border-gray-400'
            }
          `}
          role="checkbox"
          aria-checked={isSelected('camp-owner')}
          aria-label="Camp Owner"
        >
          <div className={`
            flex-shrink-0 h-6 w-6 rounded-full border-2 flex items-center justify-center
            ${isSelected('camp-owner')
              ? 'border-primary bg-primary'
              : 'border-gray-300'
            }
          `}>
            {isSelected('camp-owner') && (
              <svg className="h-4 w-4 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M5 13l4 4L19 7"></path>
              </svg>
            )}
          </div>
          
          <div className="flex-1 text-left">
            <div className="flex items-center gap-2">
              <BuildingStorefrontIcon className="h-5 w-5 text-primary" aria-hidden="true" />
              <span className="font-medium text-gray-900">Camp Owner</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              I own or manage a summer camp
            </p>
          </div>
        </button>
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

